<template>
    <div class="modal-backdrop" @click.self="$emit('close')" @keydown.esc="$emit('close')" tabindex="-1">
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="order-title">
            <header>
                <h2 id="order-title">Pedir: {{ product.name }}</h2>
                <button class="close" @click="$emit('close')" aria-label="Cerrar">×</button>
            </header>

            <form class="content" @submit.prevent="onConfirm">
                <section class="product-recap">
                    <img v-if="product.imageUrl" :src="product.imageUrl" :alt="product.name" class="thumb" />
                    <div class="product-meta">
                        <p class="name">{{ product.name }}</p>
                        <p class="price">
                            Desde <strong>{{ formatCurrency(product.priceFrom) }}</strong> c/u
                        </p>
                        <p v-if="form.quantity > 0" class="estimate">
                            Total estimado: <strong>{{ formatCurrency(product.priceFrom * form.quantity) }}</strong>
                        </p>
                    </div>
                </section>

                <label class="block">
                    <span>Cantidad</span>
                    <input v-model.number="form.quantity" type="number" min="1" step="1" required />
                </label>

                <label class="block">
                    <span>Nombre completo *</span>
                    <input v-model.trim="form.contactName" type="text" required />
                </label>

                <label class="block">
                    <span>Correo electrónico *</span>
                    <input v-model.trim="form.contactEmail" type="email" required />
                </label>

                <label class="block">
                    <span>Teléfono (opcional)</span>
                    <input v-model.trim="form.contactPhone" type="tel" />
                </label>

                <p v-if="errorMessage" class="err-msg" role="alert">{{ errorMessage }}</p>

                <small class="disclaimer">
                    El precio mostrado es referencial. Te confirmaremos el total final por correo.
                </small>

                <footer>
                    <button type="button" class="secondary" @click="$emit('close')" :disabled="submitting">
                        Cancelar
                    </button>
                    <button type="submit" class="primary" :disabled="!isValid || submitting">
                        {{ submitting ? 'Enviando…' : 'Confirmar pedido' }}
                    </button>
                </footer>
            </form>
        </div>
    </div>
</template>

<script>
import { formatCurrencyRAE } from '@/utils/numberFormat';

export default {
    name: 'CatalogOrderModal',
    props: {
        product: { type: Object, required: true },
    },
    emits: ['confirm', 'close'],
    data() {
        return {
            form: {
                quantity: 1,
                contactName: '',
                contactEmail: '',
                contactPhone: '',
            },
            submitting: false,
            errorMessage: '',
        };
    },
    computed: {
        isValid() {
            const f = this.form;
            return (
                f.quantity >= 1 &&
                f.contactName.length > 0 &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contactEmail)
            );
        },
    },
    mounted() {
        document.addEventListener('keydown', this.onEsc);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.onEsc);
    },
    methods: {
        onEsc(e) {
            if (e.key === 'Escape' && !this.submitting) this.$emit('close');
        },
        onConfirm() {
            if (!this.isValid) return;
            this.submitting = true;
            this.errorMessage = '';
            this.$emit('confirm', {
                productId: this.product.id,
                quantity: this.form.quantity,
                contactName: this.form.contactName,
                contactEmail: this.form.contactEmail,
                contactPhone: this.form.contactPhone || null,
            });
        },
        setError(message) {
            this.errorMessage = message;
            this.submitting = false;
        },
        formatCurrency(value) {
            return formatCurrencyRAE(value);
        },
    },
};
</script>

<style scoped>
.modal-backdrop {
    position: fixed; inset: 0; background: rgba(15,23,42,0.55);
    display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal {
    background: white; border-radius: 12px; max-width: 480px; width: 92%;
    max-height: 90vh; overflow-y: auto; padding: 1.5rem;
    box-shadow: 0 20px 50px rgba(0,0,0,0.3);
}
header {
    display: flex; justify-content: space-between; align-items: center;
    border-bottom: 1px solid #e2e8f0; padding-bottom: 0.5rem; margin-bottom: 1rem;
}
header h2 { margin: 0; color: #2c3e50; font-size: 1.05rem; }
.close { background: transparent; border: none; font-size: 1.5rem; cursor: pointer; color: #64748b; }
.content { display: flex; flex-direction: column; gap: 0.75rem; }
.product-recap {
    display: flex; gap: 0.75rem; align-items: center;
    background: #f8fafc; padding: 0.75rem; border-radius: 8px;
}
.thumb { width: 72px; height: 72px; object-fit: contain; border-radius: 6px; background: white; }
.product-meta { flex: 1; }
.product-meta .name { margin: 0; font-weight: 600; color: #2c3e50; }
.product-meta .price { margin: 0.2rem 0 0; font-size: 0.85rem; color: #4f5665; }
.product-meta .estimate { margin: 0.15rem 0 0; font-size: 0.85rem; color: #526d82; }
.block { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.85rem; color: #4f5665; }
input[type="number"], input[type="text"], input[type="email"], input[type="tel"] {
    border: 1px solid #cbd5e1; border-radius: 6px; padding: 0.45rem 0.6rem;
    font-size: 0.95rem; font-family: inherit; color: #2c3e50; background: white;
}
input:focus { outline: 2px solid #526d82; outline-offset: 1px; }
.err-msg {
    background: rgba(185,28,28,0.08); color: #991b1b;
    padding: 0.5rem 0.7rem; border-radius: 6px; font-size: 0.88rem;
}
.disclaimer { color: #64748b; font-style: italic; }
footer { display: flex; gap: 0.5rem; justify-content: flex-end; padding-top: 0.5rem; }
.primary {
    padding: 0.55rem 1rem; background: #526d82; color: white; border: none;
    border-radius: 6px; cursor: pointer; font-family: inherit;
}
.primary:hover:not(:disabled) { background: #3d566b; }
.primary:disabled { opacity: 0.55; cursor: not-allowed; }
.secondary {
    padding: 0.55rem 1rem; background: white; color: #2c3e50;
    border: 1px solid #cbd5e1; border-radius: 6px; cursor: pointer; font-family: inherit;
}
.secondary:disabled { opacity: 0.55; cursor: not-allowed; }
</style>
